<template>
  <div class="space-y-6">
    <!-- Header with back button and category info -->
    <div class="tw-flex items-center justify-between">
      <div class="tw-flex items-center space-x-4">
        <button
          @click="goBack"
          class="tw-flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <Icon name="chevron-left" class="w-5 h-5"/>
          <span>{{ $t('knowledge_base_back_to_knowledge_base') }}</span>
        </button>
        <div class="h-6 w-px bg-gray-300"></div>
        <div class="tw-flex flex-row">
          <h1 class="text-lg text-gray-900">
            {{ category?.name || (category === null ? $t('knowledge_base_uncategorized') : $t('knowledge_base_all_posts')) }}
          </h1>
          <p class="text-sm text-gray-500 content-center ml-3">
            {{ totalPosts }} {{ totalPosts === 1 ? $t('knowledge_base_post_singular') : $t('knowledge_base_post_plural') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Posts Grid -->
    <div class="space-y-6">
      <!-- Loading skeleton for first load -->
      <div v-if="isInitialLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <div
          v-for="n in 8"
          :key="n"
          class="h-96 bg-gray-100 rounded-lg animate-pulse"
        ></div>
      </div>

      <!-- Posts Grid -->
      <div v-else-if="posts.length > 0">
        <!-- Edit Mode: Drag and Drop Grid -->
        <div v-if="isEditMode">
          <draggable
              v-model="posts"
              group="posts"
              item-key="id"
              class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 min-h-[200px] p-4 border-2 border-dashed border-gray-200 rounded-lg transition-colors"
              :class="{ 'border-primary bg-primary/5': isDragOver }"
              :data-category-id="category?.id || 'uncategorized'"
              @start="onDragStart"
              @end="onDragEnd"
              @change="(evt) => onPostMoved(evt, category)"
              :animation="200"
          >
            <template #item="{ element: post }">
              <div class="drag-item">
                <KnowledgeBasePostCard
                    :post="post"
                    :key="post.id"
                    :show-actions="true"
                    :is-logged-in="Boolean(isLoggedIn)"
                    :current-user-id="profile?.id"
                    :community-id="communityId"
                    @edit-post="editPostClick"
                    @delete-post="deletePostClick"
                    @bookmark-toggled="handleBookmarkToggled"
                    class="h-96 cursor-move"
                />
              </div>
            </template>
            <template #header>
              <div v-if="posts.length === 0" class="col-span-full text-center py-8">
                <Icon name="upload" class="w-12 h-12 text-gray-400 mx-auto mb-3"/>
                <p class="text-gray-500">
                  {{ $t('knowledge_base_drag_posts_here') }}
                </p>
              </div>
            </template>
          </draggable>
        </div>
        
        <!-- Normal Mode: Regular Grid -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <KnowledgeBasePostCard
              v-for="post in posts"
              :key="post.id"
              :post="post"
              :show-actions="true"
              :is-logged-in="Boolean(isLoggedIn)"
              :current-user-id="profile?.id"
              :community-id="communityId"
              @edit-post="editPostClick"
              @delete-post="deletePostClick"
              @bookmark-toggled="handleBookmarkToggled"
              class="h-96"
          />
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!isInitialLoading" class="text-center py-16">
        <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="file" class="w-8 h-8 text-gray-400"/>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ $t('knowledge_base_no_posts_found') }}</h3>
        <p class="text-gray-500 max-w-md mx-auto mb-6">
          {{
            category
              ? $t('knowledge_base_no_posts_in_category', { category: category.name })
              : (category === null ? $t('knowledge_base_no_uncategorized_posts') : $t('knowledge_base_no_posts_criteria'))
          }}
        </p>
        <button
          @click="openCreatePostModal"
          class="bg-primary text-white px-4 py-2 rounded-md text-sm hover:bg-primary-lightest transition-colors"
        >
          {{ $t('knowledge_base_create_first_post') }}
        </button>
      </div>

      <!-- Loading more indicator -->
      <div
        v-if="isLoadingMore && posts.length > 0"
        class="flex justify-center py-8"
      >
        <div class="tw-flex items-center space-x-2 text-gray-500">
          <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-community"></div>
          <span>{{ $t('knowledge_base_loading_more_posts') }}</span>
        </div>
      </div>

      <!-- Load more trigger -->
      <div ref="loadMoreTrigger" class="h-10"></div>
    </div>
  </div>
</template>

<script>
  import Icon from '@/components/Icon/Icon.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import UserMixin from '@/util/UserMixin'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import { MUTATION_TYPES } from '@/store/modules/ui'
  import { deletePost as deletePostApi, fetchPosts, fetchPostsByCategory, fetchUncategorizedPosts, updatePostCategory } from '@/Domain/KnowledgeBase/Api/knowledgeBase'
  import KnowledgeBasePostCard from '@/Domain/KnowledgeBase/Components/KnowledgeBasePostCard.vue'
  import draggable from 'vuedraggable'

  export default {
    name: 'CategoryGridView',
    mixins: [TranslationsMixin, UserMixin],
    components: {
      KnowledgeBasePostCard,
      Icon,
      draggable,
    },
    props: {
      category: {
        type: Object,
        default: null
      },
      categories: {
        type: Array,
        default: () => []
      },
      searchQuery: {
        type: String,
        default: ''
      },
      communityId: {
        type: String,
        default: null
      },
      bookmarkedOnly: {
        type: Boolean,
        default: false
      },
      isEditMode: {
        type: Boolean,
        default: false
      }
    },
    emits: ['go-back', 'loading-changed'],
    data () {
      return {
        posts: [],
        isInitialLoading: true,
        isLoadingMore: false,
        hasMorePages: true,
        currentPage: 0,
        observer: null,
        // Drag and drop state
        isDragOver: false,
        draggedItem: null,
      }
    },
    computed: {
      totalPosts () {
        return this.posts.length
      }
    },
    mounted () {
      this.setupInfiniteScroll()
      this.loadPosts(true)
      // Listen for bus events from modals
      this.$bus.on('confirmAction', this.handleConfirmAction)
    },
    watch: {
      searchQuery () {
        this.loadPosts(true) // Reload when search changes
      },
      bookmarkedOnly () {
        this.loadPosts(true) // Reload when bookmark filter changes
      }
    },
    beforeUnmount () {
      if (this.observer) {
        this.observer.disconnect()
      }
      // Clean up event listeners
      this.$bus.off('confirmAction', this.handleConfirmAction)
    },
    methods: {
      handleConfirmAction (modalContext) {
        if (modalContext && modalContext.modalContextType === 'knowledge-base-post') {
          this.confirmDeletePost(modalContext.post)
        }
      },
      async confirmDeletePost (post) {
        try {
          await deletePostApi(post.id)
          await this.loadPosts(true) // Reload posts
          this.$notify({
            type: 'success',
            title: 'Success',
            text: `Post "${post.title}" deleted successfully!`
          })
        } catch (error) {
          console.error('Failed to delete post:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Failed to delete post. Please try again.'
          })
        }
      },
      async loadPosts (reset = false) {
        // Prevent concurrent loading
        if (!reset && (this.isLoadingMore || this.isInitialLoading)) {
          console.log('Already loading, skipping...')
          return
        }

        if (reset) {
          this.currentPage = 1
          this.posts = []
          this.hasMorePages = true
          this.isInitialLoading = true
          this.$emit('loading-changed', true)
        } else {
          this.isLoadingMore = true
        }

        try {
          const params = {
            page: this.currentPage,
            per_page: 12,
            communityId: this.communityId,
            bookmarked_only: this.bookmarkedOnly
          }

          // Add search filter if set
          if (this.searchQuery && this.searchQuery.trim()) {
            params.search = this.searchQuery.trim()
          }

          console.log('Loading posts with params:', params)

          let response
          if (this.category) {
            params.category_id = this.category.id
            // Load posts for specific category
            response = await fetchPostsByCategory(params)
          } else if (this.category === null) {
            // Load uncategorized posts (when category is explicitly null)
            response = await fetchUncategorizedPosts(params)
          } else {
            // Load all posts (when no category prop is passed)
            response = await fetchPosts(params)
          }

          const newPosts = response.data.data || []
          console.log('Received posts:', newPosts.length, 'Current page:', response.data.current_page, 'Last page:', response.data.last_page)

          if (reset) {
            this.posts = newPosts
          } else {
            // Check for duplicates before adding
            const existingIds = new Set(this.posts.map(post => post.id))
            const uniqueNewPosts = newPosts.filter(post => !existingIds.has(post.id))
            console.log('Adding unique posts:', uniqueNewPosts.length)
            this.posts = [...this.posts, ...uniqueNewPosts]
          }

          // Update pagination info
          this.hasMorePages = response.data.current_page < response.data.last_page
          this.currentPage = response.data.current_page + 1 // Set to next page to load

          console.log('Updated state - hasMorePages:', this.hasMorePages, 'nextPage:', this.currentPage)

        } catch (error) {
          console.error('Failed to load posts:', error)
        } finally {
          this.isInitialLoading = false
          this.isLoadingMore = false
          if (reset) {
            this.$emit('loading-changed', false)
          }
        }
      },


      setupInfiniteScroll () {
        this.$nextTick(() => {
          const loadMoreTrigger = this.$refs.loadMoreTrigger
          if (!loadMoreTrigger) return

          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !this.isLoadingMore && this.hasMorePages) {
                  this.loadPosts()
                }
              })
            },
            { threshold: 0.1 }
          )

          this.observer.observe(loadMoreTrigger)
        })
      },

      goBack () {
        this.$emit('go-back')
      },

      openCreatePostModal () {
        const modalContext = {
          categories: this.categories,
          selectedCategoryId: this.category?.id || null,
          isEditing: false
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CREATE_KNOWLEDGE_BASE_POST)
      },

      editPostClick (post) {
        const modalContext = {
          post: post,
          categories: this.categories,
          isEditing: true
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_KNOWLEDGE_BASE_POST)
      },

      deletePostClick (post) {
        const modalContext = {
          title: `Delete ${post.title}?`,
          body: 'Are you sure you want to delete this post? This action cannot be undone.',
          confirmLabel: 'Delete',
          cancelLabel: 'Cancel',
          isCancellable: true,
          modalContextType: 'knowledge-base-post',
          post: post
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },

      handleBookmarkToggled(updatedPost) {
        // Update the post data in our posts array
        const postIndex = this.posts.findIndex(post => post.id === updatedPost.id)
        if (postIndex !== -1) {
          this.posts[postIndex].isBookmarked = updatedPost.is_bookmarked
          this.posts[postIndex].bookmarks_count = updatedPost.bookmarks_count
        }

        // If we're filtering by bookmarked only and the post was unbookmarked, remove it from the list
        if (this.bookmarkedOnly && !updatedPost.is_bookmarked) {
          this.posts = this.posts.filter(post => post.id !== updatedPost.id)
        }
      },

      // Drag and drop methods
      onDragStart(evt) {
        this.isDragOver = true
        this.draggedItem = evt.item
      },

      onDragEnd(evt) {
        this.isDragOver = false
        this.draggedItem = null
      },

      async onPostMoved(evt, targetCategory) {
        // evt.added: element was added to this category
        // evt.removed: element was removed from this category
        // evt.moved: element was moved within this category

        if (evt.added) {
          const movedPost = evt.added.element
          const targetCategoryId = targetCategory?.id || null
          
          await this.updatePostCategory(movedPost.id, targetCategoryId)
          
          // Show success message
          this.$store.commit('ui/SHOW_NOTIFICATION', {
            type: 'success',
            message: this.$t('knowledge_base_post_moved_success'),
            timeout: 3000
          })
        }

        if (evt.removed) {
          // Post was removed from this category - handled by the category it was added to
        }
      },

      getCurrentCategoryId(element) {
        // Check if element is valid
        if (!element || typeof element.closest !== 'function') {
          console.warn('Invalid element passed to getCurrentCategoryId:', element)
          // Fallback to the current category ID
          return this.category?.id || null
        }

        // Find the category ID from the draggable element
        let categoryElement = element.closest('[data-category-id]')
        if (categoryElement) {
          const categoryId = categoryElement.getAttribute('data-category-id')
          return categoryId === 'uncategorized' ? null : parseInt(categoryId)
        }
        
        // Fallback to the current category ID
        return this.category?.id || null
      },

      async updatePostCategory(postId, categoryId) {
        try {
          await updatePostCategory(postId, categoryId)
          
          // Reload posts to reflect changes
          await this.loadPosts()
        } catch (error) {
          console.error('Failed to update post category:', error)
          this.$store.commit('ui/SHOW_NOTIFICATION', {
            type: 'error',
            message: this.$t('knowledge_base_post_move_error'),
            timeout: 5000
          })
        }
      }
    }
  }
</script>
